import styled from "@emotion/styled";
import pathOr from "ramda/src/pathOr";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import { OptionType } from "@atlaskit/select";
import { MouseEvent, useMemo, useState } from "react";
import { COLOR_COOL_GREY, COLOR_LIGHT_SLATE_GRAY, COLOR_MISCHKA } from "../../constants/styles";
import { Grid } from "../../UI/Grid";
import { Typography as UITypography } from "../../UI/Typography";

import { IPlacementWorksheetUnitOccupant } from "../../services/PlacementWorksheetApi";
import { ResidentMenu } from "../ResidentMenu";
import { usePlacementWorksheetSettingsContext } from "../../contexts/PlacementWorksheetContext";
import { normalize } from "../../helpers/normalize";
import { Button } from "../../UI/Button";
import { usePlacementWorksheetTableRowContext } from "./PlacementWorksheetTableRowContext";

// Todo: update types
interface ITableResidentsCellItemProps {
  resident?: IPlacementWorksheetUnitOccupant;
  roomLabelName: string;
  defaultBedroom: string;
  unitId: number;
  roomLabelId: number;
  isDisabled?: boolean;
}

const Typography = styled(UITypography)`
  line-height: 1.17;
`;

const GenderTypography = styled(Typography)`
  text-transform: capitalize;
  color: ${COLOR_COOL_GREY};
`;

const AVATAR_SIZE = 24;

const StyledButton = styled(Button)`
  padding: ${(props) => props.theme.spacing(0.5)} !important;
  align-items: center !important;
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
`;



export const TableResidentsCellItem: React.FC<ITableResidentsCellItemProps> = ({
  resident,
  // defaultBedroom,
  unitId,
  roomLabelId,
  roomLabelName,
  isDisabled
}) => {
  const { labels, colors } = usePlacementWorksheetSettingsContext();
  const { isExpanded } = usePlacementWorksheetTableRowContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const residentFullName = useMemo(() => {
    if (!resident) return "";
    return `${resident.firstName || ''} ${resident.lastName || ''}`;
  }, [
    resident?.firstName,
    resident?.lastName,
  ]);

  const roomOptions: OptionType[] = useMemo(() => {
    if (!labels) return [];
    return labels.map(({ id, label }) => ({ value: id, label }));
  }, [normalize(labels)]);

  const selectedRoomOption = useMemo(() => {
    if (resident?.roomLabelId) {
      const label = labels.find(({ id }) => resident.roomLabelId === id)?.label;
      if (!label) return null;
      return {
        value: resident.roomLabelId,
        label,
      } as OptionType;
    }
    return null;
  }, [resident?.roomLabelId, normalize(labels)]);

  const handleClick = async (event: MouseEvent<HTMLElement, globalThis.MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = useMemo(() => !!anchorEl, [anchorEl]);

  const AVATAR_BG_COLOR = {
    new: colors.newResidentColor,
    renewing: colors.renewingResidentColor,
  };

  // Render read-only view when disabled (full color, non-clickable)
  const renderReadOnlyContent = () => (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%" sx={{ padding: '4px' }}>
      <Avatar
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          bgcolor: pathOr(COLOR_MISCHKA, [resident?.status || "missing"], AVATAR_BG_COLOR),
          ...(!resident?.status && { color: COLOR_LIGHT_SLATE_GRAY }),
        }}
        variant="rounded"
      >
        <Typography variant="body2" fontWeight={700}>
          {resident ? `${resident.firstName ? resident.firstName.charAt(0) : ''}${resident.lastName ? resident.lastName.charAt(0) : ''}` : ""}
        </Typography>
      </Avatar>
      {resident && (
        <Stack alignItems="flex-start">
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%', textOverflow: 'ellipsis' }}>
            {resident == null ? "" : `${resident.firstName || ''} ${resident.lastName || ''}`}
          </Typography>
          <GenderTypography variant="body2">{resident?.gender}</GenderTypography>
        </Stack>
      )}
    </Stack>
  );

  return (
    <Grid item xs={12}>
      {isDisabled ? (
        renderReadOnlyContent()
      ) : (
        <StyledButton appearance="subtle" onClick={handleClick} isDisabled={!resident}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
            <Avatar
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                bgcolor: pathOr(COLOR_MISCHKA, [resident?.status || "missing"], AVATAR_BG_COLOR),
                ...(!resident?.status && { color: COLOR_LIGHT_SLATE_GRAY }),
              }}
              variant="rounded"
            >
              <Typography variant="body2" fontWeight={700}>
                {resident ? `${resident.firstName ? resident.firstName.charAt(0) : ''}${resident.lastName ? resident.lastName.charAt(0) : ''}` : ""}
              </Typography>
            </Avatar>
            {resident && (
              <Stack alignItems="flex-start">
                <Typography variant="body2" noWrap sx={{ maxWidth: '100%', textOverflow: 'ellipsis' }}>
                  {resident == null ? "" : `${resident.firstName || ''} ${resident.lastName || ''}`}
                </Typography>
                <GenderTypography variant="body2">{resident?.gender}</GenderTypography>
              </Stack>
            )}
          </Stack>
        </StyledButton>
      )}
      {/* Only render menu when not disabled */}
      {!isDisabled && (
        <ResidentMenu
          open={open}
          roomLabelId={roomLabelId}
          unitId={unitId}
          residentId={resident?.id}
          anchorEl={anchorEl}
          onClose={handleClose}
          title={residentFullName}
          roomOptions={roomOptions}
          selectedRoomOption={selectedRoomOption}
        />
      )}
      {isExpanded && resident?.lifestyleQuestions && resident.lifestyleQuestions.length > 0 &&
        resident.lifestyleQuestions.map((question, index) => (
          <Stack mt={1} key={index}>
            <Typography variant="caption">
              {question.tag || ''}: {question.answer || ''}
            </Typography>
          </Stack>
        ))}
    </Grid>
  );
};
